/**
 * Booking a meeting in someone else's time zone.
 *
 * A BDA types "2:00 PM" and picks Central. That is a WALL CLOCK time in that
 * zone, not a time on this machine's clock, so it has to be converted to a real
 * instant before it reaches the calendar — otherwise a meeting booked from India
 * for a US client lands hours out.
 */

export const MEETING_TIME_ZONES = [
  { id: 'America/New_York', label: 'EST / EDT — US Eastern' },
  { id: 'America/Chicago', label: 'CST / CDT — US Central' },
  { id: 'America/Denver', label: 'MST / MDT — US Mountain' },
  { id: 'America/Los_Angeles', label: 'PST / PDT — US Pacific' },
  { id: 'Europe/London', label: 'GMT / BST — UK' },
  { id: 'Europe/Berlin', label: 'CET / CEST — Central Europe' },
  { id: 'Asia/Kolkata', label: 'IST — India' },
  { id: 'Asia/Dubai', label: 'GST — Gulf' },
  { id: 'Australia/Sydney', label: 'AEST / AEDT — Sydney' },
  { id: 'UTC', label: 'UTC' }
];

/** How far ahead of UTC `timeZone` is at that instant, in milliseconds. */
const offsetAt = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = Number(part.value);
    return acc;
  }, {});
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second);
  return asUtc - date.getTime();
};

/**
 * "2026-09-01T14:00" in America/Chicago -> the matching UTC ISO string.
 *
 * Done in two passes because the offset itself depends on the instant: near a
 * daylight-saving change the first guess can sit on the wrong side of the jump.
 */
export const zonedWallClockToUtc = (wallClock, timeZone) => {
  if (!wallClock) return null;
  if (!timeZone || timeZone === 'local') return new Date(wallClock).toISOString();

  const [datePart, timePart = '00:00'] = String(wallClock).split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  if (!year || !month || !day) return null;

  const naive = Date.UTC(year, month - 1, day, hour || 0, minute || 0);
  let instant = new Date(naive - offsetAt(new Date(naive), timeZone));
  instant = new Date(naive - offsetAt(instant, timeZone));
  return instant.toISOString();
};

/** The reverse, for putting a saved meeting back into a datetime-local input. */
export const utcToZonedWallClock = (isoString, timeZone) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (!timeZone || timeZone === 'local') {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }
  const shifted = new Date(date.getTime() + offsetAt(date, timeZone));
  return shifted.toISOString().slice(0, 16);
};

/** "Tue 1 Sep 2026, 14:00 CST / CDT — US Central" */
export const describeMeetingTime = (isoString, timeZone) => {
  if (!isoString) return '';
  const zone = timeZone || 'UTC';
  const label = MEETING_TIME_ZONES.find(z => z.id === zone)?.label || zone;
  try {
    const text = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZone: zone
    }).format(new Date(isoString));
    return `${text} · ${label}`;
  } catch {
    return new Date(isoString).toString();
  }
};
